<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch } from "vue";

defineOptions({ inheritAttrs: false });

type Placement =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end";

type Positioning = "anchor" | "center";

const props = withDefaults(defineProps<{
  visible: boolean;
  triggerRef?: HTMLElement | null;
  width?: string;
  placement?: Placement;
  offset?: number;
  teleport?: boolean;
  className?: string;
  padding?: string;
  closeOnClickOutside?: boolean;
  closeOnScroll?: boolean;
  flip?: boolean;
  arrow?: boolean;
  matchTriggerWidth?: boolean;
  minWidthPx?: number;
  constrainHeight?: boolean;
  positioning?: Positioning;
}>(), {
  placement: "bottom",
  offset: 8,
  teleport: true,
  width: "w-56",
  className: "",
  padding: "py-2",
  closeOnClickOutside: true,
  closeOnScroll: false,
  flip: true,
  arrow: false,
  matchTriggerWidth: false,
  constrainHeight: false,
  positioning: "anchor",
});

const emit = defineEmits<{
  "update:visible": [value: boolean];
  "update:resolvedPlacement": [value: Placement];
  close: [];
  "popover-mouseenter": [];
  "popover-mouseleave": [];
}>();

const popoverRef = ref<HTMLElement | null>(null);
const style = ref<Record<string, string>>({});
const resolvedPlacement = ref<Placement>(props.placement);
let resizeObserver: ResizeObserver | null = null;

function getOppositeAxis(placement: Placement): Placement {
  if (placement === "bottom") return "top";
  if (placement === "bottom-start") return "top-start";
  if (placement === "bottom-end") return "top-end";
  if (placement === "top") return "bottom";
  if (placement === "top-start") return "bottom-start";
  if (placement === "top-end") return "bottom-end";
  return placement;
}

function calcPosition(placement: Placement, triggerRect: DOMRect, popoverWidth: number, popoverHeight: number) {
  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;

  switch (placement) {
    case "bottom":
      return { top: triggerRect.bottom + props.offset, left: triggerCenterX - popoverWidth / 2 };
    case "bottom-start":
      return { top: triggerRect.bottom + props.offset, left: triggerRect.left };
    case "bottom-end":
      return { top: triggerRect.bottom + props.offset, left: triggerRect.right - popoverWidth };
    case "top":
      return { top: triggerRect.top - popoverHeight - props.offset, left: triggerCenterX - popoverWidth / 2 };
    case "top-start":
      return { top: triggerRect.top - popoverHeight - props.offset, left: triggerRect.left };
    case "top-end":
      return { top: triggerRect.top - popoverHeight - props.offset, left: triggerRect.right - popoverWidth };
    case "right":
      return { top: triggerCenterY - popoverHeight / 2, left: triggerRect.right + props.offset };
    case "right-start":
      return { top: triggerRect.top, left: triggerRect.right + props.offset };
    case "right-end":
      return { top: triggerRect.bottom - popoverHeight, left: triggerRect.right + props.offset };
    case "left":
      return { top: triggerCenterY - popoverHeight / 2, left: triggerRect.left - popoverWidth - props.offset };
    case "left-start":
      return { top: triggerRect.top, left: triggerRect.left - popoverWidth - props.offset };
    case "left-end":
      return { top: triggerRect.bottom - popoverHeight, left: triggerRect.left - popoverWidth - props.offset };
  }
}

function updatePosition() {
  if (!props.triggerRef || !props.visible) return;

  const triggerRect = props.triggerRef.getBoundingClientRect();
  const minWidth = props.matchTriggerWidth ? triggerRect.width : 0;
  const popoverWidth = Math.max(popoverRef.value?.offsetWidth || 224, minWidth);
  const popoverHeight = popoverRef.value?.offsetHeight || 0;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let activePlacement = props.placement;

  if (props.flip) {
    if (activePlacement.startsWith("bottom")) {
      const spaceBelow = vh - triggerRect.bottom - props.offset - margin;
      const spaceAbove = triggerRect.top - props.offset - margin;
      if (popoverHeight > spaceBelow && spaceAbove > spaceBelow) activePlacement = getOppositeAxis(activePlacement);
    } else if (activePlacement.startsWith("top")) {
      const spaceAbove = triggerRect.top - props.offset - margin;
      const spaceBelow = vh - triggerRect.bottom - props.offset - margin;
      if (popoverHeight > spaceAbove && spaceBelow > spaceAbove) activePlacement = getOppositeAxis(activePlacement);
    }
  }

  resolvedPlacement.value = activePlacement;
  emit("update:resolvedPlacement", activePlacement);
  const position = calcPosition(activePlacement, triggerRect, popoverWidth, popoverHeight);

  let maxHeight: number | undefined;
  if (props.constrainHeight) {
    if (activePlacement.startsWith("top")) maxHeight = triggerRect.top - props.offset - margin;
    else if (activePlacement.startsWith("bottom")) maxHeight = vh - triggerRect.bottom - props.offset - margin;
    else maxHeight = vh - margin * 2;
    maxHeight = Math.max(maxHeight, 80);
  }

  const resolvedMinWidth = props.matchTriggerWidth ? minWidth : props.minWidthPx ?? 0;
  if (props.positioning === "center") {
    style.value = {
      position: "fixed",
      top: "50%",
      left: "50%",
      zIndex: "9999",
      ...(resolvedMinWidth > 0 ? { minWidth: `${resolvedMinWidth}px` } : {}),
      ...(maxHeight !== undefined ? { maxHeight: `${maxHeight}px` } : {}),
    };
    return;
  }

  style.value = {
    position: "fixed",
    top: `${Math.max(margin, Math.min(position.top, vh - popoverHeight - margin))}px`,
    left: `${Math.max(margin, Math.min(position.left, vw - popoverWidth - margin))}px`,
    zIndex: "9999",
    ...(resolvedMinWidth > 0 ? { minWidth: `${resolvedMinWidth}px` } : {}),
    ...(maxHeight !== undefined ? { maxHeight: `${maxHeight}px` } : {}),
  };
}

function handlePointerDownOutside(event: PointerEvent) {
  if (!props.visible) return;
  const target = event.target as Node | null;
  const isTrigger = !!target && !!props.triggerRef?.contains(target);
  const isPopover = !!target && !!popoverRef.value?.contains(target);

  if (!isTrigger && !isPopover) {
    emit("update:visible", false);
    emit("close");
  }
}

function handleWindowBlur() {
  // Pointer events inside an iframe do not bubble to the parent window. When
  // focus moves into an iframe outside this popover, treat it as an outside
  // interaction so teleported popovers retain the normal dismiss behavior.
  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLIFrameElement)) return;
  if (props.triggerRef?.contains(activeElement) || popoverRef.value?.contains(activeElement)) return;

  emit("update:visible", false);
  emit("close");
}

function handleScroll(event: Event) {
  if (!props.visible) return;
  if (popoverRef.value && event.composedPath().includes(popoverRef.value)) return;

  if (props.closeOnScroll) {
    emit("update:visible", false);
    emit("close");
    return;
  }

  updatePosition();
}

watch(() => props.visible, async (visible) => {
  if (visible) {
    await nextTick();
    updatePosition();
    if (props.closeOnClickOutside) window.addEventListener("pointerdown", handlePointerDownOutside, true);
    if (props.closeOnClickOutside) window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);
    if (popoverRef.value) {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(popoverRef.value);
    }
  } else {
    window.removeEventListener("pointerdown", handlePointerDownOutside, true);
    window.removeEventListener("blur", handleWindowBlur);
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", handleScroll, true);
    resizeObserver?.disconnect();
    resizeObserver = null;
  }
});

onUnmounted(() => {
  window.removeEventListener("pointerdown", handlePointerDownOutside, true);
  window.removeEventListener("blur", handleWindowBlur);
  window.removeEventListener("resize", updatePosition);
  window.removeEventListener("scroll", handleScroll, true);
  resizeObserver?.disconnect();
});
</script>

<template>
  <Teleport to="body" :disabled="!teleport">
    <transition
      enter-active-class="transition duration-200 ease-out"
      :enter-from-class="`transform scale-95 opacity-0 ${resolvedPlacement.startsWith('top') ? 'translate-y-2' : '-translate-y-2'}`"
      enter-to-class="transform scale-100 opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform scale-100 opacity-100 translate-y-0"
      :leave-to-class="`transform scale-95 opacity-0 ${resolvedPlacement.startsWith('top') ? 'translate-y-2' : '-translate-y-2'}`"
    >
      <div
        v-if="visible"
        ref="popoverRef"
        :style="teleport ? style : {}"
        class="base-popover-surface rounded-xl shadow-2xl border z-50"
        :class="[width, className, padding, arrow ? 'overflow-visible' : 'overflow-hidden', !teleport ? 'absolute mt-2' : 'fixed', constrainHeight ? 'flex flex-col' : '']"
        @mouseenter="emit('popover-mouseenter')"
        @mouseleave="emit('popover-mouseleave')"
      >
        <slot />
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/*
 * Keep the default surface theme-aware without competing with consumer
 * Tailwind utilities. `:where()` intentionally gives this fallback zero
 * specificity, so a consumer `bg-*`, `text-*`, or `border-*` class remains a
 * supported override even though the popover is teleported to body.
 */
@layer components {
  :global(:where(.base-popover-surface)) {
    background-color: var(--color-surface, #ffffff);
    color: var(--color-text, #111827);
    border-color: var(--color-border, #e5e7eb);
  }

  :global(:where(.dark) :where(.base-popover-surface)) {
    background-color: var(--color-dark-100, #111827);
    color: var(--color-dark-text, #f9fafb);
    border-color: var(--color-dark-200, #1f2937);
  }
}
</style>
